import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { v4 } from 'uuid';
import { GridCustomColDef } from '../../types';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { getDate, getFormattedDate, chipStyles } from '../../../utils/helpers';
import {
  advancedBooleanOperators,
  advancedDatePickerOperator,
  advancedEnumOperators,
  advancedOperators,
  runPickerOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { getCommissionsInfoByType } from '../../../store/GeneralStore';
import { Text } from '../../Atoms/Text';
import { handleAssetClick } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';

export const DefaultLinkGridColumnDefs = (): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({
            name: ComponentName.LINK,
            id: params.row.id,
            data: params.row
          })
        }}
      />
    ),
    hideable: false,
    pinned: 'left'
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmAccountId',
    headerName: 'RPM Account',
    sortable: true,
    width: 170,
    filterable: false,
    valueFormatter: (value, row) => row.rpmAccountNumber,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={params.row.rpmAccountNumber}
        valueFormatted={''}
        state={{
          id: '',
          type: 'rpmAccount',
          data: params.row,
          ...getMetaData({ name: ComponentName.RPM_ACCOUNT, id: params.row.rpmAccountId })
        }}
      />
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetNumber',
    headerName: 'Asset',
    sortable: true,
    width: 150,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      if (!params.row.assetNumber) return <></>;
      const type = params.row.assetSfId ? ComponentName.ASSET : ComponentName.PROVISIONAL_ASSET;
      return (
        <button
          className="flex items-center"
          type="button"
          onClick={() => handleAssetClick(params.row.assetId, type)}>
          <Text
            size="sm14"
            color="indigo">
            {params.row.assetNumber as string}
          </Text>
        </button>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetTsd',
    headerName: 'TSD',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetSupplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetCustomer',
    headerName: 'Customer',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetSalesAgent',
    headerName: 'AP',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'commissionsStatus'),
    field: 'assetCommissionStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Commissions Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'serviceStatus'),
    field: 'assetStatus',
    headerName: 'Service Status',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'createdAt',
    headerName: 'Link Added At',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'lll');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'createdBy.email',
    headerName: 'Link Added By',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    renderCell: (params: GridCellParams) => (
      <span title={params.row.createdBy?.email || ''}>{params.value as string}</span>
    ),
    valueGetter: (_value, row) => `${row?.createdBy?.firstName || ''} ${row?.createdBy?.lastName || ''}`,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetExpectedFirstCommissionRun',
    headerName: 'Exp. First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 150,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetActualFirstCommissionRun',
    headerName: 'Act. First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 150,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetExpectedDecommissionDate',
    headerName: 'Expected Decommission Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetActualDecommissionedDate',
    headerName: 'Actual Decommission Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 150,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmAccountCustomerName',
    headerName: 'RPM Customer',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmAccountSupplierName',
    headerName: 'RPM Supplier',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmSource',
    headerName: 'Source',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmAssignedRep',
    headerName: 'Assigned Rep',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmAssignedAgency',
    headerName: 'Assigned Agency',
    sortable: true,
    filterable: true,
    width: 170,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmFirstCommissionRun',
    headerName: 'First Commission Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 150,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmLastCommissionRun',
    headerName: 'Last Commission Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 150,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'candidate',
    headerName: 'Candidate',
    sortable: true,
    filterable: true,
    width: 150,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'archived',
    headerName: 'Archived',
    sortable: true,
    filterable: true,
    width: 150,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedBooleanOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'id',
    headerName: 'ID',
    sortable: true,
    filterable: true,
    width: 250,
    hideByDefault: true,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  }
];
